import { useEffect, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { FormCard, PasswordField, Button, LockIcon } from "@fawkes/ui";

import { setPassword } from "../../authProvider";
import { readResetTokenFromHash } from "../../auth/setPasswordRoute";
import { ADMIN_LOGIN_PATH } from "../../auth/loginRoute";

// jmix-7-3: the React replacement for the discarded Vaadin SetPasswordView. Reached via an
// admin-issued reset link `/set-password#<token>`. The token rides in the URL FRAGMENT (kept out of
// the Referer header + server logs); on mount we read it once, then strip the fragment from the
// address bar so it can't leak. It lives in component state only — never web storage. Composes
// @fawkes/ui primitives only (Golden Rule); additive .fawkes-auth* CSS.
//
// Anti-oracle: the backend answers a UNIFORM 400 for every token problem, so a server failure shows
// ONE "link invalid or expired" message. The operator's OWN input mistakes (empty / too short /
// mismatch) are caught client-side with specific messages and NO network call — they are not a token
// oracle, and a fix-and-retry must not burn the (still-valid) token.
const MIN_PASSWORD_LENGTH = 8;

export function SetPasswordPage() {
  const t = useTranslate();
  // Read the token once, before the fragment is stripped (initializer runs on first render only).
  const [token, setToken] = useState(() =>
    readResetTokenFromHash(globalThis.location?.hash),
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Strip the #fragment so the token does not survive in the address bar / onward navigation / a
  // copied URL. Runs after the initializer above has already captured it.
  useEffect(() => {
    const loc = globalThis.location;
    if (loc?.hash && globalThis.history?.replaceState) {
      globalThis.history.replaceState(null, "", loc.pathname + loc.search);
    }
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);

    // Client-side guards — the operator's own input, not an oracle on the token. No network call, and
    // the token is kept so a fix-and-retry works.
    if (!newPassword) {
      setError(t("auth.error.enterPassword"));
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t("auth.setPassword.error.weakPassword"));
      return;
    }
    if (newPassword !== confirm) {
      setError(t("auth.setPassword.error.confirmMismatch"));
      return;
    }

    setBusy(true);
    const result = await setPassword(token, newPassword);
    setBusy(false);
    setNewPassword("");
    setConfirm("");
    if (result.ok) {
      setToken(""); // spent — drop it
      setDone(true);
      return;
    }
    if (result.message === "network") {
      // Transient — keep the token, let them retry.
      setError(t("auth.error.network"));
      return;
    }
    // Uniform token failure: the link is invalid/expired/spent/throttled — drop the token (a retry
    // needs a fresh link) and show the single anti-oracle message.
    setToken("");
    setError(t("auth.setPassword.error.uniform"));
  }

  return (
    <main className="fawkes-auth">
      <div className="fawkes-auth__brand">{t("shell.brand")}</div>
      {done ? (
        <div className="fawkes-auth__form">
          <FormCard>
            <h1 className="fawkes-auth__heading">{t("auth.setPassword.title")}</h1>
            <p className="fawkes-auth__notice" role="status">
              {t("auth.setPassword.success")}
            </p>
            <Button onClick={() => globalThis.location?.assign(ADMIN_LOGIN_PATH)}>
              {t("auth.setPassword.toLogin")}
            </Button>
          </FormCard>
        </div>
      ) : (
        <form className="fawkes-auth__form" onSubmit={submit} noValidate>
          <FormCard>
            <h1 className="fawkes-auth__heading">{t("auth.setPassword.heading")}</h1>
            <p className="fawkes-auth__lede">{t("auth.setPassword.intro")}</p>
            <PasswordField
              label={t("auth.field.newPassword")}
              icon={<LockIcon width={16} height={16} />}
              value={newPassword}
              autoComplete="new-password"
              autoFocus
              revealLabel={t("auth.field.revealPassword")}
              hideLabel={t("auth.field.hidePassword")}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <p className="fawkes-auth__hint">{t("auth.setPassword.help.newPassword")}</p>
            <PasswordField
              label={t("auth.field.confirmPassword")}
              icon={<LockIcon width={16} height={16} />}
              value={confirm}
              autoComplete="new-password"
              revealLabel={t("auth.field.revealPassword")}
              hideLabel={t("auth.field.hidePassword")}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {error ? (
              <p className="fawkes-auth__error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={busy}>
              {busy ? t("auth.setPassword.submitting") : t("auth.setPassword.submit")}
            </Button>
          </FormCard>
        </form>
      )}
    </main>
  );
}
